import { useEffect, useMemo, useState } from "react";
import { PageContainer } from "@swot/base-components";

import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
  type CmtsGridRowAction,
} from "@components/data-grid/index.js";

import { ApiErrorPanel } from "@components/errors/index.js";
import { useApiError } from "@hooks/useApiError.js";
import { reportApi } from "@services/reportApi.js";

import {
  CmtsReportFilterPanel,
  type CmtsReportFilters,
} from "./CmtsReportFilterPanel.js";

import { CmtsReportPreviewPanel } from "./CmtsReportPreviewPanel.js";
import { CmtsReportExportPanel } from "./CmtsReportExportPanel.js";

type Row = Record<string, unknown>;

type ActiveDialog =
  | {
      readonly mode: "preview" | "export";
      readonly report: Row;
    }
  | null;

const columns: readonly CmtsGridColumn<Row>[] = [
  {
    id: "code",
    header: "Code",
    accessor: (row) =>
      row.reportCode ??
      row.code ??
      row.id ??
      "-",
    sortable: true,
    filterable: true,
  },
  {
    id: "name",
    header: "Report",
    accessor: (row) =>
      row.name ??
      row.title ??
      row.reportName ??
      "-",
    sortable: true,
    filterable: true,
  },
  {
    id: "category",
    header: "Category",
    accessor: (row) =>
      row.category ??
      row.reportCategory ??
      "General",
    sortable: true,
    filterable: true,
  },
  {
    id: "formats",
    header: "Formats",
    accessor: (row) =>
      Array.isArray(row.formats)
        ? row.formats.join(", ")
        : row.availableFormats ??
          "JSON, CSV, XLSX",
    sortable: false,
    filterable: true,
  },
  {
    id: "permission",
    header: "Permission",
    accessor: (row) =>
      row.permission ??
      row.permissionCode ??
      row.requiredPermissionCode ??
      "Backend scoped",
    sortable: true,
    filterable: true,
  },
];

export function CmtsReportCatalog() {
  const [rows, setRows] =
    useState<readonly Row[]>([]);

  const [filters, setFilters] =
    useState<CmtsReportFilters>({});

  const [loading, setLoading] =
    useState(false);

  const [activeDialog, setActiveDialog] =
    useState<ActiveDialog>(null);

  const apiError = useApiError();

  const filterKey = JSON.stringify(filters);

  useEffect(() => {
    const controller = new AbortController();

    setLoading(true);
    apiError.clearError();

    reportApi
      .getReportCatalog(
        {
          page: 1,
          pageSize: 25,
          search: filters.search,
          filters,
        },
        controller.signal,
      )
      .then((page) => {
        if (!controller.signal.aborted) {
          setRows(page.items);
        }
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          apiError.setError(error, {
            operation: "report.catalog",
            entityType: "ReportCatalog",
          });
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => {
      controller.abort();
    };
  }, [filterKey]);

  const summary = useMemo(() => {
    const exportable = rows.filter(
      (row) => row.canExport !== false,
    ).length;

    const categories = new Set(
      rows.map((row) =>
        String(
          row.category ??
          row.reportCategory ??
          "General",
        ),
      ),
    ).size;

    return {
      total: rows.length,
      exportable,
      categories,
      restricted: rows.length - exportable,
    };
  }, [rows]);

  const actions: readonly CmtsGridRowAction<Row>[] = [
    {
      id: "preview",
      label: "Preview",
      onClick: (row) => {
        setActiveDialog({
          mode: "preview",
          report: row,
        });
      },
    },
    {
      id: "export",
      label: "Export",
      onClick: (row) => {
        setActiveDialog({
          mode: "export",
          report: row,
        });
      },
      disabled: (row) =>
        row.canExport === false,
    },
  ];

  const selectedCode = String(
    activeDialog?.report.reportCode ??
    activeDialog?.report.code ??
    activeDialog?.report.id ??
    "",
  );

  const selectedName = String(
    activeDialog?.report.name ??
    activeDialog?.report.title ??
    activeDialog?.report.reportName ??
    selectedCode,
  );

  return (
    <PageContainer
      title="CMTS Reports"
      subtitle="Secure preview, analytics and export workspace."
    >
      <div className="cmts-reports-shell">
        <section className="cmts-report-hero">
          <div className="cmts-report-hero-content">
            <span className="cmts-report-hero-eyebrow">
              REPORTING WORKSPACE
            </span>

            <h1>Operational intelligence, ready when you are.</h1>

            <p>
              Preview governed CMTS reports, apply filters and
              export authorized data from one secure workspace.
            </p>
          </div>

          <div
            className="cmts-report-hero-badge"
            aria-hidden="true"
          >
            <span>CMTS</span>
            <strong>REPORTS</strong>
          </div>
        </section>

        <section className="cmts-report-stat-grid">
          <ReportStatCard
            label="Available reports"
            value={summary.total}
            tone="indigo"
          />

          <ReportStatCard
            label="Export enabled"
            value={summary.exportable}
            tone="emerald"
          />

          <ReportStatCard
            label="Categories"
            value={summary.categories}
            tone="cyan"
          />

          <ReportStatCard
            label="Restricted"
            value={summary.restricted}
            tone="amber"
          />
        </section>

        <section className="cmts-report-filter-card">
          <div className="cmts-report-section-heading">
            <div>
              <span>REFINE</span>
              <h2>Report filters</h2>
            </div>

            <p>
              Search reports or narrow the catalog by date.
            </p>
          </div>

          <CmtsReportFilterPanel
            onChange={setFilters}
          />
        </section>

        <ApiErrorPanel
          error={apiError.error}
          onRetry={() => window.location.reload()}
        />

        <section className="cmts-report-catalog-card">
          <div className="cmts-report-section-heading">
            <div>
              <span>CATALOG</span>
              <h2>Available reports</h2>
            </div>

            <p>
              Select Preview to inspect report data or Export
              to generate a downloadable file.
            </p>
          </div>

          <CmtsSmartDataGrid<Row>
            title="Report catalog"
            rows={rows}
            columns={columns}
            rowKey="id"
            defaultPageSize={10}
            searchable
            sortable
            filterable
            loading={loading}
            error={apiError.error?.message}
            actions={actions}
            emptyMessage="No reports are available for the current tenant and permission scope."
          />
        </section>
      </div>

      {activeDialog && selectedCode ? (
        <ReportDialog
          title={
            activeDialog.mode === "preview"
              ? `${selectedName} Preview`
              : `Export ${selectedName}`
          }
          subtitle={selectedCode}
          onClose={() => setActiveDialog(null)}
        >
          {activeDialog.mode === "preview" ? (
            <CmtsReportPreviewPanel
              reportCode={selectedCode}
              filters={filters}
              autoRun
            />
          ) : (
            <CmtsReportExportPanel
              reportCode={selectedCode}
              filters={filters}
              canExport={
                activeDialog.report.canExport !== false
              }
            />
          )}
        </ReportDialog>
      ) : null}
    </PageContainer>
  );
}

function ReportStatCard(props: {
  readonly label: string;
  readonly value: number;
  readonly tone:
    | "indigo"
    | "emerald"
    | "cyan"
    | "amber";
}) {
  return (
    <article
      className={`cmts-report-stat-card cmts-report-stat-card--${props.tone}`}
    >
      <div className="cmts-report-stat-icon" />

      <div>
        <strong>{props.value}</strong>
        <span>{props.label}</span>
      </div>
    </article>
  );
}

function ReportDialog(props: {
  readonly title: string;
  readonly subtitle: string;
  readonly children: React.ReactNode;
  readonly onClose: () => void;
}) {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        props.onClose();
      }
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [props.onClose]);

  return (
    <div
      className="cmts-report-dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          props.onClose();
        }
      }}
    >
      <section
        className="cmts-report-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cmts-report-dialog-title"
      >
        <header className="cmts-report-dialog-header">
          <div>
            <span>{props.subtitle}</span>

            <h2 id="cmts-report-dialog-title">
              {props.title}
            </h2>
          </div>

          <button
            type="button"
            className="cmts-report-dialog-close"
            aria-label="Close report dialog"
            onClick={props.onClose}
          >
            ×
          </button>
        </header>

        <div className="cmts-report-dialog-body">
          {props.children}
        </div>
      </section>
    </div>
  );
}